// useEscapeReplayStatus.js
// Phase 5 QA fix (Bug 1) — escape_peace-old/index.php blocks a completed
// player from starting a new run ("You have played this game already");
// the React landing page never checked this even though the backend's
// escapeReplayStatus endpoint (Backend/controllers/escapeReplayStatus.js)
// already implements it. Follows the exact same identity pattern as
// useEscapePersistence.js (adminToken + readUserId()) so there's a single
// source of truth for player identity, not a second copy of it.
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../../admin/sessionSlice';
import { readUserId } from './useEscapePersistence';
import { fetchEscapeReplayStatus } from '../services/escapeApi';

export function useEscapeReplayStatus() {
  const adminToken = useSelector(selectAdminToken);
  const userIdRef = useRef(readUserId());
  const userId = userIdRef.current;

  // No session at all (e.g. a visitor who hasn't gone through the
  // platform's login flow) — there's nothing to check, so this behaves
  // exactly like today: Begin Play is shown. This is a fail-open only for
  // "no session exists", never for an actual API failure (see the catch
  // branch below, which fails closed).
  const hasSession = Boolean(adminToken && userId);

  const [status, setStatus] = useState(hasSession ? 'loading' : 'ready'); // 'loading' | 'ready' | 'blocked' | 'error'
  const [error, setError] = useState(null);

  const check = useCallback(async () => {
    if (!hasSession) {
      setStatus('ready');
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      const res = await fetchEscapeReplayStatus({ token: adminToken, userId });
      setStatus(res?.hasPlayed && !res?.canReplay ? 'blocked' : 'ready');
    } catch (err) {
      // Fail closed: an unknown replay status must never silently allow a
      // replay — show a retryable error instead of Begin Play.
      setError(err.message || 'Could not check your play status.');
      setStatus('error');
    }
  }, [hasSession, adminToken, userId]);

  useEffect(() => {
    check();
  }, [check]);

  return { status, error, retry: check };
}
